// SavedViews — dropdown control near the ViewPicker on the Project page.
//
// Captures the current { view_type, groupBy, hiddenColumns, filter } as spec
// and POSTs it to the backend. Listing and applying restores state via the
// per-project setters (saveGroupBy, saveHidden) plus parent callbacks.

import { useEffect, useRef, useState } from "preact/hooks";
import { BookmarkPlus, ChevronDown, Eye, Globe, Lock, Trash2, Users } from "lucide-preact";
import { useViews, addView, removeView, type SavedView } from "../store/views";
import { saveGroupBy, type GroupBy } from "../lib/grouping";
import { saveHidden, type TableColumnId } from "../lib/columns";
import { useMe } from "../lib/auth";

import { Button } from "./ui/Button";

export interface ViewSpec {
  view_type: string;
  groupBy: GroupBy;
  hiddenColumns: TableColumnId[];
  filter?: string;
}

interface Props {
  projectId: number;
  /** Current view state to capture or restore. */
  currentViewType: string;
  groupBy: GroupBy;
  hiddenColumns: Set<TableColumnId>;
  filter?: string;
  /** Callbacks to restore state when a view is applied. */
  onApply: (spec: ViewSpec) => void;
}

const VISIBILITY_LABELS: Record<SavedView["visibility"], string> = {
  private: "Privat",
  shared: "Geteilt",
  project: "Projekt",
};

const VISIBILITY_ICONS: Record<SavedView["visibility"], preact.JSX.Element> = {
  private: <Lock size={14} aria-hidden="true" />,
  shared: <Users size={14} aria-hidden="true" />,
  project: <Globe size={14} aria-hidden="true" />,
};

function isValidViewType(v: unknown): v is SavedView["view_type"] {
  return v === "table" || v === "gantt" || v === "kanban" || v === "calendar";
}

function isGroupBy(v: unknown): v is GroupBy {
  return v === "phase" || v === "status" || v === "priority" || v === "assignee";
}

function isTableColumnId(v: unknown): v is TableColumnId {
  return (
    v === "status" ||
    v === "assignees" ||
    v === "labels" ||
    v === "start" ||
    v === "end" ||
    v === "priority"
  );
}

function parseSpec(raw: Record<string, unknown>): ViewSpec | null {
  const view_type = raw.view_type;
  const groupBy = raw.groupBy;
  if (!isValidViewType(view_type) || !isGroupBy(groupBy)) return null;
  const hidden = Array.isArray(raw.hiddenColumns)
    ? (raw.hiddenColumns as unknown[]).filter(isTableColumnId)
    : [];
  const filter = typeof raw.filter === "string" ? raw.filter : undefined;
  return { view_type, groupBy, hiddenColumns: hidden, filter };
}

export function SavedViews(props: Props): preact.JSX.Element {
  const { projectId, currentViewType, groupBy, hiddenColumns, filter, onApply } = props;
  const me = useMe();
  const viewsState = useViews(projectId);
  const [open, setOpen] = useState(false);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveVisibility, setSaveVisibility] =
    useState<SavedView["visibility"]>("private");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setShowSaveForm(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const handleSave = async (): Promise<void> => {
    const name = saveName.trim();
    if (!name) return;
    setSaving(true);
    try {
      const spec: ViewSpec = {
        view_type: currentViewType,
        groupBy,
        hiddenColumns: Array.from(hiddenColumns),
        filter: filter && filter !== "all" ? filter : undefined,
      };
      const vt = isValidViewType(currentViewType) ? currentViewType : "table";
      await addView(projectId, { name, view_type: vt, visibility: saveVisibility, spec: spec as unknown as Record<string, unknown> });
      setSaveName("");
      setSaveVisibility("private");
      setShowSaveForm(false);
    } catch {
      // api() toastet POST-Fehler bereits selbst; hier nur die Ablehnung
      // abschliessen (das Formular bleibt absichtlich offen, nichts zu
      // ueberschreiben).
    } finally {
      setSaving(false);
    }
  };

  const handleApply = (v: SavedView): void => {
    const spec = parseSpec(v.spec);
    if (!spec) return;
    // Persist to localStorage via the existing per-project setters.
    saveGroupBy(projectId, spec.groupBy);
    saveHidden(new Set(spec.hiddenColumns));
    onApply(spec);
    setOpen(false);
  };

  const handleDelete = async (v: SavedView, e: MouseEvent): Promise<void> => {
    e.stopPropagation();
    setDeletingId(v.id);
    try {
      await removeView(v.id, projectId);
    } catch {
      // removeView() rollt den optimistischen Entfernen-Schritt selbst
      // zurück (store/views.ts) und wirft weiter; api() toastet den
      // DELETE-Fehler bereits. Hier nur die Ablehnung abschliessen.
    } finally {
      setDeletingId(null);
    }
  };

  const myId = me.user?.id;

  return (
    <div ref={rootRef} class="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        title="Gespeicherte Ansichten"
        class="inline-flex items-center gap-1.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-1.5 text-sm font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors min-h-[36px]"
      >
        <Eye size={14} aria-hidden="true" />
        <span class="hidden sm:inline">Ansichten</span>
        <ChevronDown
          size={14}
          aria-hidden="true"
          class={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div class="absolute left-0 top-full z-40 mt-1 w-72 rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] shadow-level-3 py-2">
          {/* Saved views list */}
          <div class="px-3 pb-1">
            <p class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-1">
              Gespeicherte Ansichten
            </p>
          </div>

          {viewsState.loading && (
            <div class="px-3 py-2 text-xs text-[color:var(--color-on-surface-variant)]">
              Lade…
            </div>
          )}
          {!viewsState.loading && viewsState.items.length === 0 && (
            <div class="px-3 py-2 text-xs text-[color:var(--color-on-surface-variant)]">
              Noch keine gespeicherten Ansichten.
            </div>
          )}
          {viewsState.items.map((v) => {
            const isOwner = v.owner_id === myId;
            return (
              <div
                key={v.id}
                class="group flex items-center gap-2 px-3 py-1.5 hover:bg-[color:var(--color-surface-container-low)] cursor-pointer"
                onClick={() => handleApply(v)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleApply(v); }}
                aria-label={`Ansicht anwenden: ${v.name}`}
              >
                <span class="flex-1 truncate text-sm text-[color:var(--color-on-surface)]">
                  {v.name}
                </span>
                <span
                  class="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-label font-medium bg-[color:var(--color-surface-container-high)] text-[color:var(--color-on-surface-variant)]"
                  title={VISIBILITY_LABELS[v.visibility]}
                >
                  {VISIBILITY_ICONS[v.visibility]}
                  <span class="hidden xs:inline">{VISIBILITY_LABELS[v.visibility]}</span>
                </span>
                {isOwner && (
                  <button
                    type="button"
                    aria-label={`Ansicht löschen: ${v.name}`}
                    disabled={deletingId === v.id}
                    onClick={(e) => void handleDelete(v, e)}
                    class="opacity-0 group-hover:opacity-100 inline-flex items-center justify-center rounded p-1 text-[color:var(--color-error)] hover:bg-[color:var(--color-error-container)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-error)] transition-opacity disabled:opacity-30 min-h-[24px] min-w-[24px]"
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                )}
              </div>
            );
          })}

          <div class="my-1 border-t border-[color:var(--color-outline-variant)]" />

          {/* Save current view form */}
          {!showSaveForm ? (
            <button
              type="button"
              onClick={() => setShowSaveForm(true)}
              class="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-inset focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
            >
              <BookmarkPlus size={14} aria-hidden="true" />
              Aktuelle Ansicht speichern
            </button>
          ) : (
            <div class="px-3 py-2 flex flex-col gap-2">
              <p class="text-xs font-medium text-[color:var(--color-on-surface)]">
                Ansicht speichern
              </p>
              <input
                type="text"
                value={saveName}
                onInput={(e) => setSaveName((e.target as HTMLInputElement).value)}
                placeholder="Name…"
                maxLength={80}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2 py-1 text-base sm:text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                aria-label="Name der Ansicht"
              />
              <select
                value={saveVisibility}
                onChange={(e) =>
                  setSaveVisibility(
                    (e.target as HTMLSelectElement).value as SavedView["visibility"]
                  )
                }
                class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                aria-label="Sichtbarkeit"
              >
                <option value="private">Privat (nur ich)</option>
                <option value="shared">Geteilt (alle Mitglieder)</option>
                <option value="project">Projekt (Standard)</option>
              </select>
              <div class="flex gap-2">
                <Button
                  variant="primary"
                  size="xs"
                  class="flex-1"
                  type="button"
                  disabled={!saveName.trim() || saving}
                  onClick={() => void handleSave()}
                >
                  {saving ? "Speichern…" : "Speichern"}
                </Button>
                <button
                  type="button"
                  onClick={() => { setShowSaveForm(false); setSaveName(""); }}
                  class="rounded border border-[color:var(--color-outline-variant)] px-2 py-1 text-xs font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
